import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './AiGovernance.css';
import AiGovNav from './AiGovNav';
import { getOrgPermissions } from '../../utils/permissions';
import { AI_FRAMEWORKS, getSoa, setSoaApplicability } from './aiGovApi';

const statusBadge = (s) => {
  if (s === 'PASS') return 'aig-b-pass';
  if (s === 'AUTO_SATISFIED') return 'aig-b-auto';
  if (s === 'FAIL') return 'aig-b-fail';
  return 'aig-b-na';
};

export default function StatementOfApplicability() {
  const { t } = useTranslation();
  const { canManageFrameworks } = getOrgPermissions();
  const [params, setParams] = useSearchParams();
  const frameworkId = Number(params.get('frameworkId')) || AI_FRAMEWORKS[0].id;
  const [soa, setSoa] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSoa(await getSoa(frameworkId)); } catch { setSoa(null); } finally { setLoading(false); }
  }, [frameworkId]);

  useEffect(() => { load(); }, [load]);

  const toggleApplicable = async (row) => {
    if (row.applicable) {
      const justification = window.prompt(t('AIGovernance.exclusionPrompt', 'Justification for excluding this control:'));
      if (!justification) return;
      await setSoaApplicability({ frameworkId, controlId: row.controlId, applicable: false, justification });
    } else {
      await setSoaApplicability({ frameworkId, controlId: row.controlId, applicable: true });
    }
    load();
  };

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('AIGovernance.soaTitle', 'Statement of Applicability')}</h1>
        <p className="aig-subtitle">{t('AIGovernance.soaSubtitle', 'Per-control applicability, implementation status and crosswalk provenance.')}</p>
      </div>

      <AiGovNav />

      <div className="aig-toolbar">
        <select className="aig-btn" value={frameworkId} onChange={(e) => setParams({ frameworkId: e.target.value })}>
          {AI_FRAMEWORKS.map((f) => <option key={f.id} value={f.id}>{f.displayName}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="aig-spinner" />
      ) : !soa ? (
        <div className="aig-empty">{t('AIGovernance.soaUnavailable', 'No applicability data. Enrol in this framework first.')}</div>
      ) : (
        <>
          <div className="aig-stat-bar">
            <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.controls', 'Controls')}</div><div className="aig-stat-val">{soa.totalControls}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.applicable', 'Applicable')}</div><div className="aig-stat-val">{soa.applicableCount}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.excluded', 'Excluded')}</div><div className="aig-stat-val">{soa.excludedCount}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.implemented', 'Implemented')}</div><div className="aig-stat-val">{soa.implementedCount}</div></div>
            <div className="aig-stat"><div className="aig-stat-label">{t('AIGovernance.readiness', 'Readiness')}</div>
              <div className={`aig-stat-val ${soa.readinessPct >= 80 ? 'aig-val-ok' : soa.readinessPct >= 50 ? 'aig-val-warn' : 'aig-val-bad'}`}>{soa.readinessPct}%</div></div>
          </div>

          <table className="aig-table">
            <thead><tr>
              <th>{t('AIGovernance.control', 'Control')}</th><th>{t('AIGovernance.name', 'Name')}</th>
              <th>{t('AIGovernance.applicability', 'Applicability')}</th><th>{t('AIGovernance.status', 'Status')}</th>
              <th>{t('AIGovernance.provenance', 'Coverage')}</th><th /></tr></thead>
            <tbody>
              {soa.controls.map((r) => (
                <tr key={r.controlId}>
                  <td><strong>{r.controlRef}</strong></td>
                  <td>{r.name}{!r.applicable && r.justification && <div className="aig-muted" style={{ fontSize: '0.7rem' }}>{r.justification}</div>}</td>
                  <td>{r.applicable
                    ? <span className="aig-badge aig-b-pass">{t('AIGovernance.applicable', 'Applicable')}</span>
                    : <span className="aig-badge aig-b-na">{t('AIGovernance.excluded', 'Excluded')}</span>}</td>
                  <td><span className={`aig-badge ${statusBadge(r.implementationStatus)}`}>{r.implementationStatus}</span></td>
                  <td>{r.propagatedFrom ? <span className="aig-muted" style={{ fontSize: '0.72rem' }}>{t('AIGovernance.viaCrosswalk', 'via mapped control')} #{r.propagatedFrom}</span> : '—'}</td>
                  <td>{canManageFrameworks && (
                    <button className="aig-btn" onClick={() => toggleApplicable(r)}>
                      {r.applicable ? t('AIGovernance.exclude', 'Exclude') : t('AIGovernance.include', 'Include')}
                    </button>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
